class PredictiveSearch extends HTMLElement {
	constructor() {
		super();

		this.selectors = {
			searchQuery: "[data-query]",
			searchMessage: "[data-message]",
			input: "[data-search-input]",
			submit: 'button[type="submit"]',
			loading: "[data-spinner]",
			clear: "[data-clear-search]",
			popularSearchItems: ["[data-ps-item]"],
			moreResultIcon: "[data-more-result-icon]",
		};

		this.container = document.querySelector("[data-search-popup]");
		this.domNodes = queryDomNodes(this.selectors, this.container);
		this.cachedResults = {};
		this.transitionDuration = 300;
		this.input = this.querySelector('input[type="search"]');
		this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
		this.searchPopupSearults = this.querySelector(".m-search-popup__result");
		this.popularSearches = this.querySelector("[data-popular-searches]");
		this.searchCount = this.querySelector("[data-search-count]");
		this.latestQueryKey = null;

		this.setupEventListeners();
	}

	setPopularSearchesLink = () => {
		const { popularSearchItems } = this.domNodes;
		popularSearchItems.forEach((item) => {
			item.href = createSearchLink(item.dataset.psQuery);
		});
	};

	setupEventListeners() {
		const { clear } = this.domNodes;

		this.querySelector("form.m-search-form").addEventListener("submit", this.onFormSubmit.bind(this));
		this.input.addEventListener(
			"input",
			debounce((event) => {
				this.onChange(event);
			}, 300).bind(this)
		);
		clear.addEventListener("click", this.onClearSearch.bind(this));
		MinimogEvents.subscribe(MinimogTheme.pubSubEvents.closeSearchPopup, () => this.close(true));
	}

	getQuery() {
		return this.input.value.trim();
	}

	onChange() {
		const query = this.getQuery();

		if (query.length) {
			this.getSearchResults(query);
			return;
		}

		this.close(true);
	}

	onFormSubmit(event) {
		if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
			event.preventDefault();
		}
	}

	onClearSearch(event) {
		event.preventDefault();
		this.input.value = "";
		this.onChange();
	}

	getCacheKey(query) {
		return query.replace(/\s+/g, "-").toLowerCase();
	}

	buildSearchUrl(query, fields, unavailableProductsOption) {
		return `${window.MinimogSettings.routes.predictive_search_url}?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=${encodeURIComponent(unavailableProductsOption)}&resources[options][prefix]=last&resources[options][fields]=${encodeURIComponent(fields)}&section_id=predictive-search`;
	}

	getSectionMarkup(responseText) {
		const section = new DOMParser()
			.parseFromString(responseText, "text/html")
			.querySelector("#shopify-section-predictive-search");

		return section ? section.innerHTML : "<div data-search-items-wrapper class=\"m-search-result\"></div>";
	}

	getSearchResults(query) {
		const cacheKey = this.getCacheKey(query);

		this.latestQueryKey = cacheKey;
		this.toggleSpinnerLoading(true);
		this.toggleClearSearch(false);

		const searchByTag = this.dataset.searchByTag === "true";
		const searchByBody = this.dataset.searchByBody === "true";
		const unavailableProductsOption = this.dataset.unavailableProductsOption;
		let searchFields = "title,product_type,vendor,variants.sku,variants.title";

		if (searchByTag) {
			searchFields += ",tag";
		}

		if (searchByBody) {
			searchFields += ",body";
		}

		if (this.cachedResults[cacheKey]) {
			this.renderSearchResults(this.cachedResults[cacheKey], cacheKey);
			return;
		}

		fetch(this.buildSearchUrl(query, searchFields, unavailableProductsOption))
			.then((response) => {
				if (!response.ok) {
					throw new Error(response.status);
				}

				return response.text();
			})
			.then((responseText) => {
				const markup = this.getSectionMarkup(responseText);

				this.cachedResults[cacheKey] = markup;
				this.renderSearchResults(markup, cacheKey);
			})
			.catch((error) => {
				this.close();
				throw error;
			});
	}

	renderSearchResults(markup, cacheKey) {
		if (cacheKey !== this.latestQueryKey) {
			return;
		}

		this.predictiveSearchResults.innerHTML = markup;
		this.setAttribute("results", true);

		const itemsWrapper = this.querySelector("[data-search-items-wrapper]");
		const hasResults = itemsWrapper && itemsWrapper.childElementCount > 0;

		this.renderSearchQueryAndMessage(hasResults);
		this.toggleSpinnerLoading(false);
		this.toggleClearSearch(true);
		this.open();
	}

	renderSearchQueryAndMessage(hasResults) {
		const { input, searchQuery, searchMessage, moreResultIcon } = this.domNodes;
		const query = input.value;
		const { resultsTitle } = searchMessage.dataset;

		searchQuery.textContent = query;
		this.predictiveSearchResults.classList.remove("m:hidden");
		this.searchPopupSearults.classList.remove("m:hidden");

		if (hasResults) {
			searchMessage.textContent = resultsTitle;
			moreResultIcon.classList.add("m:hidden");
			return;
		}

		searchMessage.textContent = searchMessage.dataset.resultsTitle;
		moreResultIcon.classList.remove("m:hidden");
	}

	toggleSpinnerLoading(showLoading) {
		const { loading, submit } = this.domNodes;

		submit.style.visibility = showLoading ? "hidden" : "visible";
		loading.style.visibility = showLoading ? "visible" : "hidden";
	}

	toggleClearSearch(showClear) {
		const { clear } = this.domNodes;

		clear.style.visibility = showClear ? "visible" : "hidden";
	}

	open() {
		this.searchPopupSearults.style.setProperty(
			"--search-result-max-height",
			`${this.resultsMaxHeight || this.getResultsMaxHeight()}px`
		);
		this.setAttribute("open", true);
		this.input.setAttribute("aria-expanded", true);
		this.isOpen = true;

		if (this.popularSearches) {
			this.popularSearches.classList.add("m:hidden");
		}

		this.searchCount.classList.remove("m:hidden");
	}

	close(clearSearch = false) {
		if (clearSearch) {
			this.input.value = "";
			this.removeAttribute("results");
			this.toggleClearSearch(false);
			this.resultsMaxHeight = false;
			this.latestQueryKey = null;
		}

		this.removeAttribute("open");
		this.predictiveSearchResults.classList.add("m:hidden");
		this.searchPopupSearults.classList.add("m:hidden");

		if (this.popularSearches) {
			this.popularSearches.classList.remove("m:hidden");
		}

		this.searchCount.classList.add("m:hidden");
	}

	getResultsMaxHeight() {
		this.resultsMaxHeight =
			window.innerHeight - document.querySelector("predictive-search").getBoundingClientRect().bottom;

		return this.resultsMaxHeight;
	}
}

customElements.define("predictive-search", PredictiveSearch);